import React, { useEffect, useState } from 'react';

import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { useToast } from 'components/ui/use-toast';
import {
  adminContent,
  type RoleplayElevenLabsModelSetting,
  type VoiceRealtimeModelSetting,
  type VoiceTranscriptionModelSetting,
} from 'services/api/adminContentClient';

type ModelOption = { id: string; label: string; description: string };

const apiErrorMessage = (error: unknown, field: string): string | null => {
  const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
  const value = data?.[field];
  return Array.isArray(value) && typeof value[0] === 'string' ? value[0] : null;
};

/** Load one Operations voice setting and expose a save helper with toasts. */
function useVoiceSetting<T>(
  load: () => Promise<{ data: T }>,
  loadErrorTitle: string,
) {
  const { addToast } = useToast();
  const [setting, setSetting] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;
    load()
      .then((response) => { if (isMounted) setSetting(response.data); })
      .catch(() => {
        if (isMounted) {
          addToast({ title: loadErrorTitle, description: 'Please refresh and try again.', variant: 'destructive' });
        }
      })
      .finally(() => { if (isMounted) setIsLoading(false); });
    return () => { isMounted = false; };
  }, [addToast, load, loadErrorTitle]);

  const save = async (
    request: () => Promise<{ data: T }>,
    success: (data: T) => { title: string; description: string },
    errorTitle: string,
    errorField: string,
  ) => {
    setIsSaving(true);
    try {
      const response = await request();
      setSetting(response.data);
      addToast(success(response.data));
    } catch (error) {
      addToast({
        title: errorTitle,
        description: apiErrorMessage(error, errorField) ?? 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return { setting, isLoading, isSaving, save };
}

const SummaryItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <span className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
    <strong className="text-foreground">{value}</strong>
  </div>
);

const OptionButtons: React.FC<{
  options: ModelOption[];
  selected: string | null;
  disabled: boolean;
  onSelect: (id: string) => void;
}> = ({ options, selected, disabled, onSelect }) => (
  <>
    {options.map((option) => (
      <Button
        key={option.id}
        type="button"
        size="auto"
        variant={selected === option.id ? 'default' : 'secondary'}
        onClick={() => onSelect(option.id)}
        disabled={disabled}
        title={option.description}
      >
        {option.label}
      </Button>
    ))}
  </>
);

export const VoiceRealtimeModelCard: React.FC = () => {
  const { setting, isLoading, isSaving, save } = useVoiceSetting<VoiceRealtimeModelSetting>(
    adminContent.operations.getVoiceRealtimeModel,
    'Failed to load voice model setting',
  );
  const update = (nextModel: string | null) => save(
    () => adminContent.operations.updateVoiceRealtimeModel(nextModel),
    () => ({
      title: 'Voice realtime model updated',
      description: nextModel ? `Voice Mode now defaults to ${nextModel}.` : 'Voice Mode now uses the built-in default.',
    }),
    'Failed to update voice model',
    'realtime_model',
  );

  return (
    <Card className="w-full max-w-none" maxWidth="none">
      <CardHeader>
        <CardTitle>Module Voice Realtime model</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 items-stretch">
        <p className="text-sm text-muted-foreground">
          Default OpenAI Realtime model for Module Voice. Doesn't apply to GPT Live 1. Staff debug sessions can still override it for testing.
        </p>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading voice model setting...</p>
        ) : setting ? (
          <>
            <div className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-3">
              <SummaryItem label="Effective" value={setting.effective_realtime_model} />
              <SummaryItem label="Saved default" value={setting.global_realtime_model_override ?? 'Built-in default'} />
              <SummaryItem label="Built-in" value={setting.environment_realtime_model} />
            </div>
            <div className="flex flex-wrap gap-2">
              <OptionButtons
                options={setting.realtime_model_options}
                selected={setting.global_realtime_model_override}
                disabled={isSaving}
                onSelect={(id) => { void update(id); }}
              />
              {setting.global_realtime_model_override ? (
                <Button type="button" size="auto" variant="secondary" onClick={() => { void update(null); }} disabled={isSaving}>
                  Use built-in default
                </Button>
              ) : null}
            </div>
            {isSaving ? <p className="text-sm text-muted-foreground">Saving voice model setting...</p> : null}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Voice model setting is unavailable.</p>
        )}
      </CardContent>
    </Card>
  );
};

export const ModuleVoiceTranscriptionModelCard: React.FC = () => {
  const { setting, isLoading, isSaving, save } = useVoiceSetting<VoiceTranscriptionModelSetting>(
    adminContent.operations.getVoiceTranscriptionModel,
    'Failed to load voice transcription model setting',
  );
  const update = (nextModel: string | null) => save(
    () => adminContent.operations.updateVoiceTranscriptionModel(nextModel),
    () => ({
      title: 'Voice transcription model updated',
      description: nextModel
        ? `New Module Voice Realtime sessions now use ${nextModel}.`
        : 'New Module Voice Realtime sessions now use GPT Transcribe.',
    }),
    'Failed to update voice transcription model',
    'transcription_model',
  );

  return (
    <Card className="w-full max-w-none" maxWidth="none">
      <CardHeader>
        <CardTitle>Module Voice transcription model</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 items-stretch">
        <p className="text-sm text-muted-foreground">
          Transcription for new or reset Module Voice Realtime sessions. Existing sessions keep their model. GPT Live 1 supplies its own transcripts, and Roleplay keeps its separate configuration.
        </p>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading voice transcription model setting...</p>
        ) : setting ? (
          <>
            <div className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-3">
              <SummaryItem label="New-session default" value={setting.effective_transcription_model} />
              <SummaryItem
                label="Saved default"
                value={setting.global_transcription_model_override ?? 'Built-in GPT Transcribe default'}
              />
              <SummaryItem label="Built-in model" value={setting.environment_transcription_model} />
            </div>
            <div className="flex flex-wrap gap-2">
              <OptionButtons
                options={setting.transcription_model_options}
                selected={setting.effective_transcription_model}
                disabled={isSaving}
                onSelect={(id) => { void update(id); }}
              />
              {setting.global_transcription_model_override ? (
                <Button type="button" size="auto" variant="secondary" onClick={() => { void update(null); }} disabled={isSaving}>
                  Use GPT Transcribe default
                </Button>
              ) : null}
            </div>
            {isSaving ? <p className="text-sm text-muted-foreground">Saving voice transcription model setting...</p> : null}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Voice transcription model setting is unavailable.</p>
        )}
      </CardContent>
    </Card>
  );
};

export const RoleplayVoiceModelCard: React.FC = () => {
  const { setting, isLoading, isSaving, save } = useVoiceSetting<RoleplayElevenLabsModelSetting>(
    adminContent.operations.getRoleplayElevenLabsModel,
    'Failed to load roleplay voice model setting',
  );
  const update = (nextModel: string | null) => save(
    () => adminContent.operations.updateRoleplayElevenLabsModel(nextModel),
    (data) => ({
      title: 'Roleplay voice model updated',
      description: nextModel
        ? `ElevenLabs roleplay voices now use ${nextModel}.`
        : `ElevenLabs roleplay voices now use the built-in default, ${data.default_model_id}.`,
    }),
    'Failed to update roleplay voice model',
    'model_id',
  );

  return (
    <Card className="w-full max-w-none" maxWidth="none">
      <CardHeader>
        <CardTitle>Roleplay Voice Model</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 items-stretch">
        <p className="text-sm text-muted-foreground">
          ElevenLabs model used to stream every WiseFriend roleplay voice. Saved voices that don't support the chosen model can't be added below.
        </p>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading roleplay voice model setting...</p>
        ) : setting ? (
          <>
            <div className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
              <SummaryItem label="Effective" value={setting.model_id} />
              <SummaryItem label="Saved default" value={setting.saved_model_id ?? 'Built-in default'} />
            </div>
            <div className="flex flex-wrap gap-2">
              <OptionButtons
                options={setting.model_options}
                selected={setting.saved_model_id}
                disabled={isSaving}
                onSelect={(id) => { void update(id); }}
              />
              {setting.saved_model_id ? (
                <Button type="button" size="auto" variant="secondary" onClick={() => { void update(null); }} disabled={isSaving}>
                  Use built-in default
                </Button>
              ) : null}
            </div>
            {isSaving ? <p className="text-sm text-muted-foreground">Saving roleplay voice model setting...</p> : null}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Roleplay voice model setting is unavailable.</p>
        )}
      </CardContent>
    </Card>
  );
};
